import { css } from '@emotion/react';
import { Trans, useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Accordion, useFormField, Text, ChecklistField, Button, styles } from '@frontend/design-system';
import { useLocationPicker } from '../store';
import { LocationPickerTestingIds } from '../testing-ids';
import { LocationPickerTrackingIds } from '../tracking-ids';
import { LocationRadioField } from './shared/location-radio';

interface LocationGroupSectionProps {
  children: React.ReactNode;
  groupId: string;
  groupLocations: string[];
  getScopeName: (scope: string, type?: 'location' | 'group' | 'org') => string;
  renderLocationInfo?: (locationId: string) => JSX.Element | null;
}

interface LocationGroupContentProps {
  groupId: string;
  groupLocations: string[];
  getScopeName: (scope: string, type?: 'location' | 'group' | 'org') => string;
  renderLocationInfo?: (locationId: string) => JSX.Element | null;
}

export const LocationGroupContent = ({
  groupId,
  groupLocations,
  getScopeName,
  renderLocationInfo,
}: LocationGroupContentProps) => {
  const {
    inFlightLocationIds,
    setInFlightGroupId,
    setInFlightLocationIds,
    setInFlightOrganizationId,
    groupViewId,
    organizationViewId,
    setGroupViewId,
    inFlightGroupId,
    hasSingleLocationGroup,
  } = useLocationPicker([
    'inFlightLocationIds',
    'setInFlightOrganizationId',
    'setInFlightGroupId',
    'setInFlightLocationIds',
    'groupViewId',
    'organizationViewId',
    'setGroupViewId',
    'inFlightGroupId',
    'hasSingleLocationGroup',
  ]);

  const checklistField = useFormField(
    {
      type: 'checklist',
      value: groupViewId === groupId ? inFlightLocationIds : [],
    },
    [inFlightLocationIds, groupViewId, groupId]
  );

  const selectedGroupLocations = checklistField.value;

  if (groupLocations.length === 0) {
    return <LocationGroupEmptyContent />;
  }

  return (
    <>
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: ${theme.spacing(2)};
        `}
      >
        <Text size={hasSingleLocationGroup ? 'large' : 'medium'} weight='bold'>
          Locations
        </Text>
        <SelectDeselect
          selectedGroupLocations={selectedGroupLocations}
          groupLocations={groupLocations}
          groupId={groupId}
        />
      </div>
      <ChecklistField
        {...checklistField}
        css={css`
          min-width: 0;
        `}
        onChange={(e) => {
          setInFlightOrganizationId(organizationViewId ?? '');
          setGroupViewId(groupId);
          // @ts-ignore
          const newValue = e?.value;
          if (inFlightGroupId !== groupId) {
            // Considering that inFlightLocationIds are on a global state, we need to reset it in order to avoid
            // selecting locations from different groups
            setInFlightLocationIds([newValue]);
          } else {
            setInFlightLocationIds(
              checklistField.value.includes(newValue)
                ? checklistField.value.filter((location) => location !== newValue)
                : [...checklistField.value, newValue]
            );
          }

          setInFlightGroupId(groupId);
        }}
        label=''
        name='locationCheckList'
      >
        {groupLocations.map((location) => (
          <ChecklistField.Option
            css={css`
              > label {
                min-width: 0;
              }
            `}
            key={location}
            name={location}
          >
            <span
              css={css`
                display: flex;
                align-items: center;
                gap: ${theme.spacing(1)};
                width: 100%;
                min-width: 0;
              `}
            >
              <span css={[styles.truncate]}>{getScopeName(location, 'location')} </span>
              <span>{renderLocationInfo?.(location)}</span>
            </span>
          </ChecklistField.Option>
        ))}
      </ChecklistField>
    </>
  );
};

const LocationGroupEmptyContent = () => {
  const { t } = useTranslation('location-picker');

  return (
    <Text size='medium' color='subdued'>
      <Trans t={t}>
        This location group contains locations that you are not authorized to access. To manage your organization's
        settings, click <strong>'Apply'</strong>.
      </Trans>
    </Text>
  );
};

export const LocationGroupSectionSimpleView = ({
  groupId,
  groupLocations,
  getScopeName,
  renderLocationInfo,
}: LocationGroupContentProps) => {
  const { hasSingleLocationGroup } = useLocationPicker(['hasSingleLocationGroup']);

  return !hasSingleLocationGroup ? (
    <>
      <Text
        css={css`
          margin-bottom: ${theme.spacing(2)};
        `}
        size='large'
        weight='bold'
      >
        Location Groups
      </Text>
      <article
        css={css`
          border: 1px solid ${theme.colors.neutral20};
          border-radius: ${theme.borderRadius.medium};
          overflow: hidden;
        `}
      >
        <header
          css={css`
            padding: ${theme.spacing(2)};
            background-color: ${theme.colors.neutral5};
          `}
        >
          <Text as='h6' size='large' weight='bold'>
            {getScopeName(groupId, 'group')}
          </Text>
        </header>
        <section
          data-testid='location-picker-group-section'
          css={css`
            padding: ${theme.spacing(2)};
          `}
        >
          <LocationGroupContent
            renderLocationInfo={renderLocationInfo}
            groupId={groupId}
            groupLocations={groupLocations}
            getScopeName={getScopeName}
          />
        </section>
      </article>
    </>
  ) : (
    <section
      css={css`
        padding: ${theme.spacing(1)};
      `}
    >
      <LocationGroupContent
        renderLocationInfo={renderLocationInfo}
        groupId={groupId}
        groupLocations={groupLocations}
        getScopeName={getScopeName}
      />
    </section>
  );
};

export const LocationGroupSection = ({
  children,
  groupId,
  groupLocations,
  getScopeName,
  renderLocationInfo,
}: LocationGroupSectionProps) => {
  const {
    inFlightGroupId,
    setInFlightGroupId,
    setInFlightLocationIds,
    setGroupViewId,
    organizationViewId,
    setInFlightOrganizationId,
  } = useLocationPicker([
    'inFlightGroupId',
    'setInFlightOrganizationId',
    'setInFlightGroupId',
    'setInFlightLocationIds',
    'setGroupViewId',
    'organizationViewId',
  ]);

  const isGroupSelected = inFlightGroupId === groupId;

  return (
    <div>
      <Accordion.Item value={groupId} title='Location Group'>
        <Accordion.Header>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: ${theme.spacing(1)};
            `}
          >
            <LocationRadioField
              testId={LocationPickerTestingIds.groupRadio(groupId)}
              isSelected={isGroupSelected}
              id={`location-group-section-${groupId}`}
              onSelected={() => {
                setInFlightOrganizationId(organizationViewId ?? '');
                setInFlightGroupId(groupId);
                setInFlightLocationIds(groupLocations);
                setGroupViewId(groupId);
              }}
              trackingId={LocationPickerTrackingIds.mainGroup}
            />
            <Text weight='bold' size='large' as='h6' color={inFlightGroupId === groupId ? 'primary' : 'default'}>
              {children}
            </Text>
            <LocationsSelectedCount groupId={groupId} />
          </div>
        </Accordion.Header>
        <Accordion.Body>
          <LocationGroupContent
            renderLocationInfo={renderLocationInfo}
            groupId={groupId}
            groupLocations={groupLocations}
            getScopeName={getScopeName}
          />
        </Accordion.Body>
      </Accordion.Item>
    </div>
  );
};

const LocationsSelectedCount = ({ groupId }: { groupId: string }) => {
  const { inFlightGroupId, inFlightLocationIds } = useLocationPicker(['inFlightGroupId', 'inFlightLocationIds']);

  if (inFlightGroupId !== groupId || inFlightLocationIds.length === 0) {
    return null;
  }

  return (
    <Text
      size='small'
      css={css`
        padding: ${theme.spacing(0, 0.5)};
        min-width: 16px;
        text-align: center;
        background: ${theme.colors.primary10};
        color: ${theme.colors.primary70};
        border-radius: ${theme.borderRadius.small};
        position: relative;
      `}
    >
      {inFlightLocationIds.length}
    </Text>
  );
};

type SelectDeselectProps = {
  selectedGroupLocations: string[];
  groupLocations: string[];
  groupId: string;
};

const SelectDeselect = ({ selectedGroupLocations, groupLocations, groupId }: SelectDeselectProps) => {
  const { t } = useTranslation();
  const { setInFlightGroupId, setInFlightLocationIds, organizationViewId, setInFlightOrganizationId } =
    useLocationPicker([
      'setInFlightGroupId',
      'setInFlightLocationIds',
      'organizationViewId',
      'setInFlightOrganizationId',
    ]);

  const handleDeselectAll = () => {
    setInFlightGroupId(groupId);
    setInFlightLocationIds([]);
  };

  const handleSelectAll = () => {
    setInFlightGroupId(groupId);
    setInFlightLocationIds(groupLocations);
    setInFlightOrganizationId(organizationViewId ?? '');
  };

  if (selectedGroupLocations.length === groupLocations.length) {
    return (
      <Button
        onClick={handleDeselectAll}
        variant='tertiary'
        trackingId={LocationPickerTrackingIds.deselectAll('main-view')}
      >
        {t('Deselect All')}
      </Button>
    );
  }

  return (
    <Button onClick={handleSelectAll} variant='tertiary' trackingId={LocationPickerTrackingIds.selectAll('main-view')}>
      {t('Select All')}
    </Button>
  );
};
